import { useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { setUnauthorizedHandler } from './api/client.js';
import { setOwnerUnauthorizedHandler } from './api/cloud-client.js';
import { Layout } from './components/Layout.js';
import { RequireAuth } from './components/RequireAuth.js';
import { ConflictsPage } from './pages/Conflicts.js';
import { DashboardPage } from './pages/Dashboard.js';
import { LoginAdminPage } from './pages/LoginAdmin.js';
import { ModulePlaceholder } from './pages/ModulePlaceholder.js';
import { RoomsPage } from './pages/RoomsPage.js';
import { AlloggiatiPage } from './pages/alloggiati/AlloggiatiPage.js';
import { CassaPage } from './pages/cassa/CassaPage.js';
import { ComponentsDemo } from './pages/dev/ComponentsDemo.js';
import { DevHome } from './pages/dev/DevHome.js';
import { DndDemo } from './pages/dev/DndDemo.js';
import { IconsDemo } from './pages/dev/IconsDemo.js';
import { LayoutPreview } from './pages/dev/LayoutPreview.js';
import { TokensDemo } from './pages/dev/TokensDemo.js';
import { TypographyDemo } from './pages/dev/TypographyDemo.js';
import { ImpostazioniPage } from './pages/impostazioni/ImpostazioniPage.js';
import { LoginPage } from './pages/login/LoginPage.js';
import { ManutenzionePage } from './pages/manutenzione/ManutenzionePage.js';
import { OspitiPage } from './pages/ospiti/OspitiPage.js';
import { OwnerDashboardPage } from './pages/owner/OwnerDashboardPage.js';
import { OwnerHotelDetailPage } from './pages/owner/OwnerHotelDetailPage.js';
import { OwnerLoginPage } from './pages/owner/OwnerLoginPage.js';
import { RequireOwner } from './pages/owner/RequireOwner.js';
import { PlanningPage } from './pages/planning/PlanningPage.js';
import { PrenotazioniPage } from './pages/prenotazioni/PrenotazioniPage.js';
import { ReportPage } from './pages/report/ReportPage.js';
import { RoomServiceAdminPage } from './pages/room-service/RoomServiceAdminPage.js';
import { useAuth } from './stores/auth.js';
import { useOwnerSession } from './stores/owner.js';

export function App() {
  // HM-384 cluster login PIN: setup handler 401 globale al mount.
  // Quando un endpoint protetto risponde 401 (cookie hm_access scaduto o
  // assente), pulisce il cache user in localStorage. Il redirect a /login
  // resta gestito da RequireAuth (useQuery /auth/me -> Navigate state.from).
  useEffect(() => {
    setUnauthorizedHandler(() => {
      useAuth.getState().logout();
    });
    // F2-a: handler 401 owner dedicato. Su sessione cloud scaduta/assente,
    // pulisce la session store owner; RequireOwner redirige a /owner/login.
    setOwnerUnauthorizedHandler(() => {
      useOwnerSession.getState().clear();
    });
    return () => {
      setUnauthorizedHandler(null);
      setOwnerUnauthorizedHandler(null);
    };
  }, []);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        {/* HM-386 cluster login PIN: fallback admin email+password */}
        <Route path="/login-admin" element={<LoginAdminPage />} />

        <Route
          element={
            <RequireAuth>
              <Layout />
            </RequireAuth>
          }
        >
          {/* Modulo 1 (cluster 15): Dashboard preserva implementazione esistente
              durante il redesign per non rompere walk-in / check-out interattivi.
              Sara' rimpiazzata in cluster 15. */}
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/camere" element={<RoomsPage />} />
          <Route path="/conflicts" element={<ConflictsPage />} />

          {/* HM-395 cluster 17: Planning Gantt camere x tempo. */}
          <Route path="/planning" element={<PlanningPage />} />
          {/* HM-500 Modulo 3 Prenotazioni lista (Spec v2.6) */}
          <Route path="/prenotazioni" element={<PrenotazioniPage />} />
          <Route path="/ospiti" element={<OspitiPage />} />
          {/* HM-485 cluster 18 Modulo 5 Cassa: KPI + movimenti + Z close */}
          <Route path="/cassa" element={<CassaPage />} />
          <Route path="/report" element={<ReportPage />} />
          {/* HM-511 cluster 24 Modulo 7 Room Service admin: Kanban ordini */}
          <Route path="/room-service" element={<RoomServiceAdminPage />} />
          {/* HM-441 cluster 23 Manutenzione Guasti: registro guasti aperti/risolti. */}
          <Route path="/manutenzione" element={<ManutenzionePage />} />
          {/* HM-495 Schedina Alloggiati Questura (Spec §10): compliance art.109 TULPS */}
          <Route path="/alloggiati" element={<AlloggiatiPage />} />
          <Route
            path="/knx"
            element={
              <ModulePlaceholder
                title="KNX & Automazioni"
                cluster={19}
                description="Mapping group address, configurazione accessi, automazioni cascade visual builder."
              />
            }
          />
          <Route path="/impostazioni" element={<ImpostazioniPage />} />
        </Route>

        {/* HM-308: pagine demo design-system fuori da RequireAuth.
            Espongono solo UI statica (no dati API), accessibili senza login
            cosi' si possono testare via Playwright e verificare visivamente
            durante development senza un backend up. */}
        <Route path="/dev" element={<DevHome />} />
        <Route path="/dev/tokens" element={<TokensDemo />} />
        <Route path="/dev/typography" element={<TypographyDemo />} />
        <Route path="/dev/icons" element={<IconsDemo />} />
        <Route path="/dev/dnd" element={<DndDemo />} />
        <Route path="/dev/components" element={<ComponentsDemo />} />
        <Route path="/dev/layout-preview" element={<LayoutPreview />} />

        {/* F2-a: area owner (cloud) — chrome dedicato, FUORI dal Layout
            operatore (Pi). /owner/login email+password+TOTP -> /owner. */}
        <Route path="/owner/login" element={<OwnerLoginPage />} />
        <Route
          path="/owner"
          element={
            <RequireOwner>
              <OwnerDashboardPage />
            </RequireOwner>
          }
        />
        {/* F2-c: gestione hotel — anagrafica/camere/listini/utenti. */}
        <Route
          path="/owner/hotels/:tenantId"
          element={
            <RequireOwner>
              <OwnerHotelDetailPage />
            </RequireOwner>
          }
        />

        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
